import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getAllPackages, PACKAGE_TYPES } from '../services/packageService';
import { createSubscription, getUserActiveSubscription } from '../services/subscriptionService';
import LoadingSpinner from './LoadingSpinner';

const ActivatePackageByEmailModal = ({ isOpen, onClose, initialEmail = '', onSuccess }) => {
  const [emailInput, setEmailInput] = useState(initialEmail);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [activeSub, setActiveSub] = useState(null);

  const [availablePackages, setAvailablePackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);

  const [selectedPackageId, setSelectedPackageId] = useState('custom');
  const [customPackageData, setCustomPackageData] = useState({
    name: 'Admin Special Package',
    duration: 30,
    maxApplications: 50,
    maxJobPostings: 10,
    price: 0,
  });

  const [activating, setActivating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Sync initialEmail prop if changed
  useEffect(() => {
    if (initialEmail) {
      setEmailInput(initialEmail);
    }
  }, [initialEmail]);

  // Fetch packages on modal open
  useEffect(() => {
    if (isOpen) {
      fetchPackages();
      if (initialEmail) {
        handleSearchUser(initialEmail);
      }
    } else {
      resetForm();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    setLoadingPackages(true);
    try {
      const res = await getAllPackages();
      if (res.success) {
        setAvailablePackages(res.packages || []);
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoadingPackages(false);
    }
  };

  const resetForm = () => {
    setEmailInput('');
    setSearching(false);
    setSearchError('');
    setFoundUser(null);
    setActiveSub(null);
    setSelectedPackageId('custom');
    setCustomPackageData({
      name: 'Admin Special Package',
      duration: 30,
      maxApplications: 50,
      maxJobPostings: 10,
      price: 0,
    });
    setActivating(false);
    setSuccessMessage('');
  };

  const handleSearchUser = async (emailToSearch = emailInput) => {
    const cleanEmail = (emailToSearch || '').trim();
    if (!cleanEmail) {
      setSearchError('Please enter an email address.');
      return;
    }

    setSearching(true);
    setSearchError('');
    setFoundUser(null);
    setActiveSub(null);
    setSuccessMessage('');

    try {
      let userDoc = null;

      // 1. Try exact match
      const q1 = query(collection(db, 'users'), where('email', '==', cleanEmail));
      const snap1 = await getDocs(q1);

      if (!snap1.empty) {
        userDoc = { id: snap1.docs[0].id, ...snap1.docs[0].data() };
      } else {
        // 2. Try lowercase match
        const q2 = query(collection(db, 'users'), where('email', '==', cleanEmail.toLowerCase()));
        const snap2 = await getDocs(q2);

        if (!snap2.empty) {
          userDoc = { id: snap2.docs[0].id, ...snap2.docs[0].data() };
        } else {
          // 3. In-memory fallback across all users
          const allUsersSnap = await getDocs(collection(db, 'users'));
          const match = allUsersSnap.docs.find(
            (d) => (d.data().email || '').toLowerCase() === cleanEmail.toLowerCase()
          );
          if (match) {
            userDoc = { id: match.id, ...match.data() };
          }
        }
      }

      if (!userDoc) {
        setSearchError(`No user found with email: "${cleanEmail}"`);
        setSearching(false);
        return;
      }

      setFoundUser(userDoc);

      // Check current active subscription
      const subResult = await getUserActiveSubscription(userDoc.id);
      if (subResult.success && subResult.subscription) {
        setActiveSub(subResult.subscription);
      }

      // Auto select first relevant package if available
      const relevant = availablePackages.filter((pkg) => {
        if (userDoc.userType === 'jobseeker') return pkg.type === PACKAGE_TYPES.JOB_SEEKER;
        if (userDoc.userType === 'employer') return pkg.type === PACKAGE_TYPES.EMPLOYER;
        return true;
      });

      if (relevant.length > 0) {
        setSelectedPackageId(relevant[0].id);
      } else if (availablePackages.length > 0) {
        setSelectedPackageId(availablePackages[0].id);
      } else {
        setSelectedPackageId('custom');
      }

    } catch (err) {
      console.error('Error searching user:', err);
      setSearchError('Error finding user: ' + err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleActivatePackage = async () => {
    if (!foundUser) {
      alert('Please search and select a user first.');
      return;
    }

    let pkgToAssign = null;

    if (selectedPackageId === 'custom') {
      pkgToAssign = {
        id: 'custom_admin_activation',
        name: customPackageData.name || 'Admin Custom Package',
        type: foundUser.userType === 'employer' ? PACKAGE_TYPES.EMPLOYER : PACKAGE_TYPES.JOB_SEEKER,
        duration: parseInt(customPackageData.duration) || 30,
        price: parseFloat(customPackageData.price) || 0,
        maxApplications: parseInt(customPackageData.maxApplications) || 0,
        maxJobPostings: parseInt(customPackageData.maxJobPostings) || 0,
        features: ['Activated by SuperAdmin'],
      };
    } else {
      pkgToAssign = availablePackages.find((p) => p.id === selectedPackageId);
      if (!pkgToAssign) {
        alert('Selected package not found.');
        return;
      }
    }

    setActivating(true);
    setSuccessMessage('');

    try {
      const subscriptionData = {
        userId: foundUser.id,
        userEmail: foundUser.email,
        userName: `${foundUser.firstName || ''} ${foundUser.lastName || ''}`.trim() || foundUser.displayName || 'User',
        userPhone: foundUser.phone || '',
        packageId: pkgToAssign.id,
        packageName: pkgToAssign.name,
        packageType: pkgToAssign.type || foundUser.userType || 'jobseeker',
        price: pkgToAssign.price || 0,
        duration: pkgToAssign.duration || pkgToAssign.durationDays || 30,
        features: pkgToAssign.features || [],
        maxApplications: pkgToAssign.maxApplications || (foundUser.userType === 'jobseeker' ? 50 : 0),
        maxJobPostings: pkgToAssign.maxJobPostings || (foundUser.userType === 'employer' ? 10 : 0),
        featuredJobs: pkgToAssign.featuredJobs || 0,
        resumeTemplates: pkgToAssign.resumeTemplates || 0,
        profileBoost: pkgToAssign.profileBoost || false,
        candidateSearch: pkgToAssign.candidateSearch || false,
        analyticsAccess: pkgToAssign.analyticsAccess || false,
        userType: foundUser.userType || 'jobseeker',
        paymentId: 'manual_superadmin_activation',
      };

      const result = await createSubscription(subscriptionData);

      if (result.success) {
        // Also update paymentId & isApproved directly on user doc to ensure full access
        try {
          const userRef = doc(db, 'users', foundUser.id);
          await updateDoc(userRef, {
            paymentId: 'manual_superadmin_activation',
            isApproved: true,
          });
        } catch (uErr) {
          console.warn('User doc update error:', uErr);
        }

        // Also update seekerProfiles or companies collection if exists
        try {
          if (foundUser.userType === 'jobseeker') {
            const seekerRef = doc(db, 'seekerProfiles', foundUser.id);
            await updateDoc(seekerRef, { isApproved: true });
          } else if (foundUser.userType === 'employer') {
            const companyRef = doc(db, 'companies', foundUser.id);
            await updateDoc(companyRef, { isApproved: true });
          }
        } catch (pErr) {
          console.warn('Profile doc approval update note:', pErr.message);
        }

        setSuccessMessage(`🎉 Package "${pkgToAssign.name}" successfully activated for ${foundUser.email}!`);

        // Refresh active subscription state
        const updatedSub = await getUserActiveSubscription(foundUser.id);
        if (updatedSub.success && updatedSub.subscription) {
          setActiveSub(updatedSub.subscription);
        }

        if (onSuccess) {
          onSuccess(foundUser, pkgToAssign);
        }
      } else {
        alert('Failed to activate package: ' + result.error);
      }
    } catch (err) {
      console.error('Error activating package:', err);
      alert('Error activating package: ' + err.message);
    } finally {
      setActivating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-2xl shadow-2xl border border-gray-100 relative my-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100"
          title="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 mb-6 border-b pb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-2xl shadow-md">
            ⚡
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Activate Package by Email</h2>
            <p className="text-sm text-gray-500">SuperAdmin tool to manually activate packages for any user</p>
          </div>
        </div>

        {/* Search Section */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Enter User Email Address <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
              placeholder="e.g. user@gmail.com"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
            />
            <button
              onClick={() => handleSearchUser()}
              disabled={searching}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {searching ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Searching...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search User
                </>
              )}
            </button>
          </div>

          {searchError && (
            <p className="mt-3 text-sm text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200 font-medium">
              ⚠️ {searchError}
            </p>
          )}
        </div>

        {/* Found User Details */}
        {foundUser && (
          <div className="space-y-6">
            <div className="bg-white border-2 border-green-200 rounded-xl p-4 shadow-sm relative bg-gradient-to-br from-green-50/50 to-white">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900">
                      {foundUser.firstName} {foundUser.lastName}
                    </h3>
                    <span className={`px-3 py-0.5 rounded-full text-xs font-bold ${
                      foundUser.userType === 'employer'
                        ? 'bg-purple-100 text-purple-800 border border-purple-200'
                        : 'bg-blue-100 text-blue-800 border border-blue-200'
                    }`}>
                      {foundUser.userType === 'employer' ? 'Employer / Company' : 'Job Seeker'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">{foundUser.email}</p>
                  <p className="text-xs text-gray-500 mt-1">Phone: {foundUser.phone || 'N/A'} | User ID: {foundUser.id}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold ${
                    foundUser.paymentId ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                  }`}>
                    {foundUser.paymentId ? '✓ Account Active' : '⚡ No Active Payment'}
                  </span>
                </div>
              </div>

              {/* Active Subscription Info if existing */}
              {activeSub && (
                <div className="mt-3 pt-3 border-t border-green-200 text-xs text-green-900 flex items-center justify-between bg-green-100/60 p-2.5 rounded-lg">
                  <div>
                    <span className="font-bold">Current Active Sub:</span> {activeSub.packageName}
                  </div>
                  <div>
                    <span className="font-semibold">Valid Till:</span> {activeSub.endDate?.toDate ? activeSub.endDate.toDate().toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              )}
            </div>

            {/* Package Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                Select Package to Activate:
              </label>

              {loadingPackages ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                  {/* System Packages */}
                  {availablePackages.map((pkg) => (
                    <label
                      key={pkg.id}
                      className={`flex items-start p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedPackageId === pkg.id
                          ? 'border-blue-600 bg-blue-50/60 shadow-sm'
                          : 'border-gray-200 hover:border-blue-300 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="selectedPackage"
                        value={pkg.id}
                        checked={selectedPackageId === pkg.id}
                        onChange={() => setSelectedPackageId(pkg.id)}
                        className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-gray-900 text-sm">{pkg.name}</span>
                          <span className="text-xs font-bold bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full">
                            ₹{pkg.price || 0}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5">
                          Duration: {pkg.duration || pkg.durationDays || 30} Days | Type: {pkg.type || 'Standard'}
                          {pkg.maxApplications ? ` | Max Apps: ${pkg.maxApplications}` : ''}
                          {pkg.maxJobPostings ? ` | Max Jobs: ${pkg.maxJobPostings}` : ''}
                        </p>
                      </div>
                    </label>
                  ))}

                  {/* Custom Package Option */}
                  <label
                    className={`flex items-start p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                      selectedPackageId === 'custom'
                        ? 'border-purple-600 bg-purple-50/60 shadow-sm'
                        : 'border-gray-200 hover:border-purple-300 bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name="selectedPackage"
                      value="custom"
                      checked={selectedPackageId === 'custom'}
                      onChange={() => setSelectedPackageId('custom')}
                      className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-purple-900 text-sm">⚙️ Custom Admin Package</span>
                        <span className="text-xs font-bold bg-purple-100 text-purple-800 px-2.5 py-0.5 rounded-full">
                          Custom Limits
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">Specify custom validity days, limits and features</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Custom Package Fields if Custom is selected */}
            {selectedPackageId === 'custom' && (
              <div className="bg-purple-50/70 border border-purple-200 rounded-xl p-4 space-y-3">
                <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider">Custom Package Parameters</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Package Name</label>
                    <input
                      type="text"
                      value={customPackageData.name}
                      onChange={(e) => setCustomPackageData({ ...customPackageData, name: e.target.value })}
                      className="w-full p-2 text-sm border rounded-lg bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Duration (Days)</label>
                    <input
                      type="number"
                      min="1"
                      value={customPackageData.duration}
                      onChange={(e) => setCustomPackageData({ ...customPackageData, duration: e.target.value })}
                      className="w-full p-2 text-sm border rounded-lg bg-white"
                    />
                  </div>

                  {foundUser.userType === 'jobseeker' ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Max Job Applications</label>
                      <input
                        type="number"
                        min="1"
                        value={customPackageData.maxApplications}
                        onChange={(e) => setCustomPackageData({ ...customPackageData, maxApplications: e.target.value })}
                        className="w-full p-2 text-sm border rounded-lg bg-white"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Max Job Postings</label>
                      <input
                        type="number"
                        min="1"
                        value={customPackageData.maxJobPostings}
                        onChange={(e) => setCustomPackageData({ ...customPackageData, maxJobPostings: e.target.value })}
                        className="w-full p-2 text-sm border rounded-lg bg-white"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Package Price (₹)</label>
                    <input
                      type="number"
                      min="0"
                      value={customPackageData.price}
                      onChange={(e) => setCustomPackageData({ ...customPackageData, price: e.target.value })}
                      className="w-full p-2 text-sm border rounded-lg bg-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Success Message Banner */}
            {successMessage && (
              <div className="bg-emerald-50 border-2 border-emerald-400 text-emerald-900 p-4 rounded-xl text-sm font-semibold flex items-start gap-2 shadow-sm animate-pulse">
                <span className="text-xl">✅</span>
                <div>{successMessage}</div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-gray-600 font-semibold border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleActivatePackage}
                disabled={activating}
                className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold rounded-xl hover:from-emerald-700 hover:to-green-700 transition-all shadow-lg text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {activating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Activating Package...
                  </>
                ) : (
                  <>
                    ⚡ Activate Package Now
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivatePackageByEmailModal;
